<template>
    <div
      class="admin-layout min-vh-100 navbar-app"
      :data-admin-theme="adminThemeKey"
      :style="adminThemeStyle"
    >
      <!-- NAVBAR -->
      <nav class="navbar navbar-expand-lg navbar-dark navbar-fixed admin-navbar-themed" data-tour="base-navbar">
        <div class="container-fluid">
          <!-- Brand -->
          <router-link class="navbar-brand fw-bold" :to="{ name: 'Login' }" title="Accueil de la gestion">
            <img src="../assets/logo.png" style="width: 30px;" />
          </router-link>

          <router-link
            v-if="showSitesShortcut"
            class="btn btn-outline-light btn-sm navbar-icon-button navbar-sites-shortcut ms-2"
            :to="{ name: 'Sites' }"
            title="Mes sites"
            aria-label="Accéder à mes sites"
          >
            <i class="fa fa-store"></i>
          </router-link>

          <router-link
            v-if="showCurrentSiteShortcut"
            class="btn btn-outline-light btn-sm current-site-button ms-2"
            :to="{ name: 'Dashboard' }"
            title="Accéder au site actuel"
          >
            <i class="fa fa-arrow-right me-1"></i>
            {{ shopName }}
          </router-link>

          <CurrentDateTime class="ms-auto me-3 base-navbar-date-time" />
          <UserMenu :show-settings="false" />
  
        </div>
      </nav>
  
      <!-- CONTENU -->
      <main class="container py-4" data-tour="page-content">
        <div v-if="canUseBaseLayout">
          <AppBreadcrumbs />
          <PageTutorialLauncher />
          <slot name="content"></slot>
        </div>
  
        <!-- Message si ce n'est pas un gérant -->
        <div v-else class="alert alert-warning mt-4">
          <h5 class="alert-heading mb-2">Accès restreint</h5>
          <p class="mb-0">
            Cette section est réservée au <strong>gérant</strong> ou à un support autorisé.
            Veuillez contacter votre administrateur si vous pensez qu’il s’agit d’une
            erreur.
          </p>
        </div>
      </main>
      <AppFooter />
    </div>
  </template>
  
  <script>
  import AppFooter from '@/components/AppFooter.vue';
  import UserMenu from '@/components/UserMenu.vue';
  import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue';
  import CurrentDateTime from '@/components/CurrentDateTime.vue';
  import PageTutorialLauncher from '@/components/PageTutorialLauncher.vue';
  import {
    adminThemeStyle as buildAdminThemeStyle,
    getAdminTheme,
  } from '@/support/adminThemes';
  import { hasPermission, routePermission } from '@/support/permissions';
  import { isMultiSiteAdmin } from '@/support/multiSiteAdmin';

  export default {
    name: 'BaseLayout',
    components: {
      AppFooter,
      UserMenu,
      AppBreadcrumbs,
      CurrentDateTime,
      PageTutorialLauncher,
    },
    data() {
      return {};
    },
    computed: {
      currentUser() {
        return this.$store.getters['auth/currentUser'];
      },
      isGerant() {
        const user = this.currentUser;
        return user && user.role === 'gerant';
      },
      canUseBaseLayout() {
        return this.isGerant
          || hasPermission(this.currentUser, 'settings.view')
          || hasPermission(this.currentUser, 'shops.view')
          || hasPermission(this.currentUser, routePermission(this.$route));
      },
      shopName() {
        return this.$store.getters['shop/shopName'];
      },
      currentShopId() {
        return this.$store.getters['shop/currentShopId'];
      },
      userSettings() {
        return this.$store.getters['userSettings/settings'] || {};
      },
      adminTheme() {
        return getAdminTheme(this.userSettings?.appearance?.admin_theme);
      },
      adminThemeKey() {
        return this.adminTheme.key;
      },
      adminThemeStyle() {
        return buildAdminThemeStyle(this.adminThemeKey);
      },
      showCurrentSiteShortcut() {
        return this.$route.name === 'Sites' && this.currentShopId && this.shopName;
      },
      showSitesShortcut() {
        return isMultiSiteAdmin(this.currentUser);
      },
      // sites() {
      //   return this.$store.getters['shop/sites'];
      // },
    },
    async mounted() {
      // if (this.currentUser) {
      //   try {
      //     await this.$store.dispatch('shop/fetchSites');
      //   } catch (e) {
      //     console.warn('Erreur lors du chargement des sites', e);
      //   }
      // }
    },
    methods: {},
  };
  </script>
  
  <style scoped>
  .admin-layout {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background-color: var(--suite241-page-bg, var(--bs-body-bg));
  }

  .admin-layout main {
    flex: 1 1 auto;
  }
  
  /* NAVBAR FIXE STYLE "LOGICIEL" */
  .navbar-fixed {
    position: sticky;
    top: 0;
    z-index: 1030;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  }

  .navbar-app .admin-navbar-themed {
    background: var(--suite241-admin-nav-bg, #0d6efd);
  }
  
  /* Espace en haut pour ne pas que le main passe sous la navbar */
  .admin-layout main {
    padding-top: 4.5rem;
  }
  
  /* Conteneur app pour les styles de navbar */
  .navbar-app .navbar {
    padding-top: 0.35rem;
    padding-bottom: 0.35rem;
  }
  
  /* Brand (nom de l'app) */
  .navbar-app .navbar-brand {
    text-transform: uppercase;
    font-weight: 700;
    font-size: 0.95rem;
    letter-spacing: 0.08em;
  }
  
  /* Liens du menu (onglets) */
  .navbar-app .nav-link {
    text-transform: uppercase;
    font-size: 0.78rem;
    font-weight: 600;
    padding: 0.55rem 0.95rem;
    margin-right: 0.25rem;
    border-radius: 0.6rem 0.6rem 0 0;
    color: rgba(255, 255, 255, 0.68);
    position: relative;
    transition:
      color 0.2s ease,
      background-color 0.2s ease,
      box-shadow 0.2s ease,
      transform 0.1s ease;
  }
  
  /* Hover */
  .navbar-app .nav-link:hover {
    color: #ffffff;
    background-color: rgba(255, 255, 255, 0.08);
  }
  
  /* Onglet actif — style logiciel bien marqué */
  .navbar-app .nav-link.active {
    color: #ffffff !important;
    background-color: rgba(0, 0, 0, 0.15);
    box-shadow: inset 0 -2px 6px rgba(0, 0, 0, 0.35);
    transform: translateY(1px);
  }
  
  /* Trait lumineux en haut de l'onglet actif */
  .navbar-app .nav-link.active::before {
    content: '';
    position: absolute;
    top: 0;
    left: 12%;
    width: 76%;
    height: 3px;
    background-color: #ffffff;
    border-radius: 999px;
    opacity: 0.95;
  }
  
  /* Dropdown : on garde lisible, pas en full uppercase */
  .navbar-app .dropdown-menu .dropdown-item {
    text-transform: none;
    font-size: 0.8rem;
  }
  
  /* Texte utilisateur à droite */
  .navbar-app .text-white.small {
    font-size: 0.78rem;
  }
  
  /* Bouton déconnexion un peu plus doux */
  .navbar-app .btn-outline-light.btn-sm {
    border-radius: 999px;
    padding-inline: 0.9rem;
    font-size: 0.78rem;
  }

  .navbar-app .current-site-button {
    align-items: center;
    display: inline-flex;
    max-width: min(52vw, 280px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .navbar-app .navbar-sites-shortcut {
    border: 0;
    color: #ffffff;
    transition: background-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  }

  .navbar-app .navbar-sites-shortcut i {
    font-size: 1.12rem;
    transition: transform 0.18s ease;
  }

  .navbar-app .navbar-sites-shortcut:hover,
  .navbar-app .navbar-sites-shortcut:focus-visible {
    background-color: rgba(255, 255, 255, 0.14);
    box-shadow: 0 0 0 0.16rem rgba(255, 255, 255, 0.24);
    color: #ffffff;
    transform: translateY(-2px);
  }

  .navbar-app .navbar-sites-shortcut:hover i,
  .navbar-app .navbar-sites-shortcut:focus-visible i {
    animation: navbar-action-pop 0.34s ease-out;
  }

  @keyframes navbar-action-pop {
    0% { transform: scale(1) rotate(0); }
    55% { transform: scale(1.18) rotate(-8deg); }
    100% { transform: scale(1) rotate(0); }
  }

  @media (prefers-reduced-motion: reduce) {
    .navbar-app .navbar-sites-shortcut,
    .navbar-app .navbar-sites-shortcut i {
      transition: none;
    }

    .navbar-app .navbar-sites-shortcut:hover i,
    .navbar-app .navbar-sites-shortcut:focus-visible i {
      animation: none;
    }
  }

  .base-navbar-date-time {
    flex: 0 0 auto;
  }

  .base-navbar-date-time :deep(.current-date-time) {
    border: 0;
  }

  @media (max-width: 575.98px) {
    .base-navbar-date-time {
      margin: 0.75rem 0 0 !important;
      order: 3;
    }
  }
  </style>
  
